import React, { useEffect } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import { tipSlotProps } from '../quick-links/tip-font';
import { CreditScope } from './hooks/useLifetimeCredits';
import {
  boundedCustomDays,
  customDayMax,
  expiryChoiceLabel,
  ExpireChoice,
  PASS_TYPE_FIELD_LABEL,
  PASS_TYPE_OPTION,
} from './gift-sent';

type SendGiftFieldsProps = {
  scope: CreditScope;
  onScope: (scope: CreditScope) => void;
  showCount: boolean;
  count: number;
  onCount: (count: number) => void;
  cardCount: number;
  available: number | null;
  expireValue: ExpireChoice;
  expireOptions: ExpireChoice[];
  onExpire: (value: string) => void;
  customDays: number;
  onCustomDays: (days: number) => void;
  daysLeft: number | null;
  extrasValue: ExpireChoice;
  onExtras: (value: string) => void;
  extrasCustomDays: number;
  onExtrasCustomDays: (days: number) => void;
  message: string;
  onMessage: (value: string) => void;
  // eslint-disable-next-line react/require-default-props
  showMessage?: boolean;
  // eslint-disable-next-line react/require-default-props
  showExtras?: boolean;
  // eslint-disable-next-line react/require-default-props
  scopeLocked?: boolean;
  // eslint-disable-next-line react/require-default-props
  tips?: {
    scope?: string;
    count?: string;
    expire?: string;
    customDays?: string;
  };
  // eslint-disable-next-line react/require-default-props
  emptyMessage?: string;
};

function labeled(text: string, tip?: string): React.ReactNode {
  if (!tip) return text;
  return (
    <span>
      {text}
      <Tooltip title={tip} describeChild slotProps={tipSlotProps} enterTouchDelay={0}>
        <Box
          component="span"
          onMouseDown={(event) => event.preventDefault()}
          sx={{
            display: 'inline-flex',
            ml: 0.5,
            color: 'var(--qc-shell-muted, #9fb0c8)',
            verticalAlign: 'middle',
            lineHeight: 0,
          }}
        >
          <InfoOutlinedIcon aria-hidden sx={{ fontSize: 16 }} />
        </Box>
      </Tooltip>
    </span>
  );
}

export const SendGiftFields = ({
  scope,
  onScope,
  showCount,
  count,
  onCount,
  cardCount,
  available,
  expireValue,
  expireOptions,
  onExpire,
  customDays,
  onCustomDays,
  daysLeft,
  extrasValue,
  onExtras,
  extrasCustomDays,
  onExtrasCustomDays,
  message,
  onMessage,
  showMessage = true,
  showExtras = true,
  scopeLocked = false,
  tips,
  emptyMessage,
}: SendGiftFieldsProps): JSX.Element => {
  const dayMax = Math.max(customDayMax(daysLeft), 1);
  useEffect(() => {
    if (expireValue !== 'custom') return;
    const next = boundedCustomDays(customDays, daysLeft);
    if (next !== customDays) onCustomDays(next);
  }, [expireValue, customDays, daysLeft, onCustomDays]);
  useEffect(() => {
    if (extrasValue !== 'custom') return;
    const next = boundedCustomDays(extrasCustomDays, daysLeft);
    if (next !== extrasCustomDays) onExtrasCustomDays(next);
  }, [extrasValue, extrasCustomDays, daysLeft, onExtrasCustomDays]);
  const stockEmpty = available === 0;
  const overLimit = available != null && available > 0 && count > available;
  let helperText = '';
  if (overLimit) helperText = `You only have ${available} gift cards to give.`;
  else if (available != null && available > 0) helperText = `You can send up to ${available}.`;
  else if (stockEmpty) helperText = emptyMessage || 'You do not have a gift card of this type.';
  const warnHelp = overLimit || (stockEmpty && Boolean(emptyMessage));
  return (
    <>
      <TextField
        select
        label={labeled(PASS_TYPE_FIELD_LABEL, tips?.scope)}
        value={scope}
        disabled={scopeLocked}
        onChange={(event) => onScope(event.target.value as CreditScope)}
        helperText={!showCount && available === 0 ? helperText : undefined}
      >
        <MenuItem value="SINGLE">{PASS_TYPE_OPTION.SINGLE}</MenuItem>
        <MenuItem value="DUAL">{PASS_TYPE_OPTION.DUAL}</MenuItem>
      </TextField>
      {showCount && (
        <TextField
          type="number"
          label={labeled('Number of Gift Cards', tips?.count)}
          value={count}
          disabled={stockEmpty}
          onChange={(event) => onCount(Math.max(0, Math.floor(Number(event.target.value) || 0)))}
          inputProps={available == null || stockEmpty ? { min: 0 } : { min: 1, max: available }}
          helperText={helperText || undefined}
          FormHelperTextProps={warnHelp ? { className: 'qc-stock-warning' } : undefined}
        />
      )}
      <TextField
        select
        label={labeled('Expires', tips?.expire)}
        value={expireValue}
        onChange={(event) => onExpire(event.target.value)}
      >
        {expireOptions.map((choice) => (
              <MenuItem key={choice} value={choice}>{expiryChoiceLabel(choice, daysLeft)}</MenuItem>
        ))}
      </TextField>
      {expireValue === 'custom' && (
        <TextField
          type="number"
          label={labeled('Custom days', tips?.customDays)}
          value={customDays}
          onChange={(event) => onCustomDays(Number(event.target.value))}
          inputProps={{ min: 1, max: dayMax }}
        />
      )}
      {showExtras && cardCount >= 2 && (
        <>
          <TextField
            select
            label="Time to share extras"
            value={extrasValue}
            onChange={(event) => onExtras(event.target.value)}
          >
            {expireOptions.map((choice) => (
              <MenuItem key={`extra-${choice}`} value={choice}>{expiryChoiceLabel(choice, daysLeft)}</MenuItem>
            ))}
          </TextField>
          {extrasValue === 'custom' && (
            <TextField
              type="number"
              label="Extra share days"
              value={extrasCustomDays}
              onChange={(event) => onExtrasCustomDays(Number(event.target.value))}
              inputProps={{ min: 1, max: dayMax }}
            />
          )}
        </>
      )}
      {showMessage && (
        <TextField
          label="Optional message"
          value={message}
          onChange={(event) => onMessage(event.target.value.slice(0, 280))}
          helperText={`${message.length}/280`}
        />
      )}
    </>
  );
};
